import React, { FC, ReactElement, useEffect, useRef, useCallback, useState } from 'react';
import { graphql } from 'gatsby';
import SwiperCore, { Pagination, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import classNames from 'classnames';
import 'swiper/swiper.scss';
import 'swiper/components/pagination/pagination.scss';

import useScreenRecognition from 'hooks/useScreenRecognition';
import GatsbyImage from 'common/GatsbyImage';
import DangerouslySetInnerHtml from 'common/DangerouslySetInnerHtml';
import RelatedCarouselNavigation from 'components/RelatedCarousel/RelatedCarouselNavigation';
import { TABLET_BREAKPOINT } from 'utils/constants';

import { getSlidesPerView } from './utils';
import { SLIDE_OFFSET } from './constants';
import './ImageWithTextItems.scss';
import { IPropsImageWithTextItems } from './models';

SwiperCore.use([Navigation, Pagination]);

const ImageWithTextItems: FC<IPropsImageWithTextItems> = ({
  imageWithTextItems,
  carousel,
  customClass,
  ariaLabelPrev = 'prev',
  ariaLabelNext = 'next',
}): ReactElement | null => {
  const { windowWidth } = useScreenRecognition();
  const navPrevRef = useRef<HTMLButtonElement>(null);
  const navNextRef = useRef<HTMLButtonElement>(null);

  const [init, setInit] = useState(false);

  const isInitHadler = useCallback((): void => {
    setInit(!init);
  }, [init]);

  const isTabletUp = windowWidth > TABLET_BREAKPOINT;

  useEffect(() => {
    if (!windowWidth) {
      return;
    }

    getSlidesPerView(windowWidth);
  }, [windowWidth]);

  if (!isTabletUp && carousel) {
    return (
      <div
        className={classNames('image-with-text-items', {
          [`image-with-text-items--${customClass}`]: customClass,
        })}
      >
        <div className="image-with-text-items__carousel">
          <Swiper
            wrapperTag="ul"
            slidesPerView={1.2}
            spaceBetween={SLIDE_OFFSET}
            pagination={{ type: 'progressbar' }}
            navigation={{
              prevEl: navPrevRef.current,
              nextEl: navNextRef.current,
            }}
            watchOverflow
            onSwiper={isInitHadler}
          >
            {imageWithTextItems.map(({ properties: { image, imageAlt } }, idx) => (
              <SwiperSlide key={`iwti-slide-${idx}`} tag="li">
                <GatsbyImage
                  className="image-with-text-items__list-item-image image-with-text-items__list-item-image--large"
                  image={image}
                  alt={imageAlt}
                />
              </SwiperSlide>
            ))}
          </Swiper>
          <RelatedCarouselNavigation
            ariaLabelPrev={ariaLabelPrev}
            ariaLabelNext={ariaLabelNext}
            navPrevRef={navPrevRef}
            navNextRef={navNextRef}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames('image-with-text-items', {
        [`image-with-text-items--${customClass}`]: customClass,
      })}
    >
      <ul className="image-with-text-items__list">
        {imageWithTextItems.map(({ properties: { image, imageAlt, text } }) => (
          <li className="image-with-text-items__list-item" key={imageAlt}>
            <GatsbyImage
              className={classNames('image-with-text-items__list-item-image', {
                'image-with-text-items__list-item-image--large': carousel,
              })}
              image={image}
              alt={imageAlt}
            />
            <DangerouslySetInnerHtml
              html={text}
              className="image-with-text-items__list-item-text"
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

export const query = graphql`
  fragment FragmentImageWithTextItems on TImageWithTextItems {
    structure
    properties {
      carousel
      imageWithTextItems {
        properties {
          text
          image {
            ...FragmentGatsbyProps
            gatsbyImage {
              childImageSharp {
                fluid(maxWidth: 524, quality: 70) {
                  ...GatsbyImageSharpFluid_withWebp
                }
              }
            }
          }
          imageAlt
          link {
            url
            name
            target
            queryString
          }
        }
      }
      customClass
    }
  }
`;

export default ImageWithTextItems;
